/**
 * `/audiences/:datasetId` — one uploaded list, at whatever stage it is at.
 *
 * A dataset is a small state machine and this screen is its face:
 *
 *   pending     the file was read but no mapping was confirmed — so the mapping
 *               form is here, and nothing has been imported
 *   processing  the import is running somewhere else; poll and show the counts
 *   ready       the summary, the rejected-row report, and the contacts
 *   failed      the reason, said plainly, and a way to try again
 *
 * The header always says which **sheet** the dataset came from when the file
 * had more than one, because two lists from one workbook are otherwise
 * indistinguishable — and telling them apart is the whole reason they were kept
 * separate in the first place.
 *
 * Two things this screen refuses to do: assume the counts on a row it loaded a
 * minute ago are still true (anything in flight is polled), and present an
 * import as clean without saying what was rejected. `rejectedCount` and the
 * report are shown together, always, including when the answer is zero.
 *
 * ## What the shorter copy is not allowed to lose
 *
 * The card descriptions here used to re-derive, in prose, the state machine at
 * the top of this header and the mapping rules that `ColumnMappingForm` now
 * explains behind its own disclosure. Two readers were being served by one set
 * of sentences and only one of them was on the screen. The reasoning is in the
 * headers; what a card says now is what its own contents mean.
 *
 * Three claims survive at full strength, because a person reading only this
 * screen would otherwise be wrong:
 *
 * - **Every row is accounted for.** Rows in the sheet, imported and rejected
 *   are three separate figures and are never collapsed into a pass/fail. The
 *   rejected report is fetched and rendered even when the count is zero —
 *   "none rejected" is a claim, and it is worth making from the report rather
 *   than from a counter that could disagree with it.
 * - **A rejection names its row and its column.** "Invalid file" is not a
 *   defect report; row 412, column `mobile`, the value, and what happened is.
 *   Those four columns of the table are the deliverable, not decoration. The
 *   card says "each is reported with…" rather than "every row is here": the
 *   table is capped and says so underneath, and two reasons — an empty row, a
 *   file past the row limit — carry no column at all, so a promise of a column
 *   on every line would be false on the rows most likely to be seen first.
 * - **Unmapped columns were preserved.** Stated where the extras are shown and
 *   again above the contacts, because that is the promise slice 1 made about
 *   somebody's spreadsheet and this is the screen that proves it was kept.
 *
 * ## Slice 66: the summary is a figure, a ledger and a mapping
 *
 * "What the import produced" used to be three equal mono numbers — green
 * imported, amber rejected — over a two-column list of field names. It is
 * the Blueprint's shape now, and each part of it keeps a promise above:
 *
 * - **One headline figure**, the contacts the import produced, in a brand
 *   `MetricPanel`, with its denominator in words ("from 2,400 rows in the
 *   sheet") and a meter of the same ratio. Contacts and rows are the same
 *   unit here — one row is at most one contact — which is why this screen
 *   may draw that bar and the segment screen may not draw its equivalent.
 * - **The three counts stay three**, as the panel's "every row accounted
 *   for" ledger: rows, imported, rejected, side by side so the sum is in
 *   plain sight, rejected shown even at zero. The colours came off them —
 *   a count is not a verdict — and the verdict moved into words: when rows
 *   were rejected, a compact warning `Callout` says how many and points at
 *   the report, which remains the place a rejection is explained.
 * - **The mapping is a read-only `MappingList`** — the same rows the mapping
 *   step draws, frozen: each contact field with the column that fed it, or
 *   "No column", and every unmapped column as its own row, kept in extras.
 *   That is the extras promise made visible per column rather than as a
 *   comma-joined sentence, which is why the sentence no longer lists them.
 *   The match shares the mapping step shows are not repeated here: after
 *   the import the rejected-row report is the better evidence.
 *
 * Per-field coverage — how many of the imported contacts actually have an
 * email, say — would belong on these rows and is not drawn: the API does not
 * report it yet, and a share computed here from the sample would be a guess.
 */
import {
  CONTACT_FIELD_VALUES,
  DEFAULT_PAGE_SIZE,
  REJECTED_ROW_REASON_VALUES,
  contactSortSchema,
  unmappedColumns,
  type Contact,
  type ContactExtras,
  type Dataset,
  type ExtraValue,
  type RejectedRowReport,
} from '@wend/shared';
import {
  Badge,
  Callout,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  DataTable,
  FieldChip,
  Input,
  Kicker,
  MappingList,
  MappingRow,
  MappingSource,
  MetricPanel,
  MetricSection,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHeader,
  TableHeaderCell,
  TableRow,
  buttonVariants,
  formatFileSize,
  type DataTableColumn,
  type DataTableSort,
} from '@wend/ui';
import { ArrowLeft, FileSpreadsheet, Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';

import { AppLayout } from '../../components/AppLayout';
import { DatasetStatusBadge } from '../../components/DatasetStatusBadge';
import { ErrorState } from '../../components/ErrorState';
import { FormAlert } from '../../components/FormAlert';
import { ImportProgress } from '../../components/ImportProgress';
import { listContacts } from '../../lib/api/contacts';
import { fetchDataset, fetchRejectedRows } from '../../lib/api/datasets';
import { errorMessage } from '../../lib/api/errors';
import {
  CONTACT_FIELD_LABEL,
  DATASET_STATUS_DISPLAY,
  REJECTED_ROW_REASON_DISPLAY,
} from '../../lib/dataset-display';
import { formatCount, formatDate, formatDateTime, pluralise } from '../../lib/format';
import { useAsyncData } from '../../lib/use-async-data';
import { ColumnMappingForm } from './ColumnMappingForm';

/** Typing pauses this long before the contact list re-queries. */
const SEARCH_DEBOUNCE_MS = 300;

export function DatasetDetailPage() {
  const { datasetId } = useParams<{ datasetId: string }>();

  // The route always supplies it; this is the type system's question, not a
  // state the app can reach, so it goes back to the list rather than erroring.
  if (datasetId === undefined) return <Navigate to="/audiences" replace />;

  return <DatasetDetail datasetId={datasetId} />;
}

function DatasetDetail({ datasetId }: { datasetId: string }) {
  const { data, error, loadingFirst, reload } = useAsyncData(
    (signal) => fetchDataset(datasetId, signal),
    [datasetId],
  );

  /**
   * The dataset as this screen has changed it since the server last spoke —
   * confirming a mapping moves it to `processing` immediately. Cleared whenever
   * a fresh copy arrives, so the server always wins in the end.
   */
  const [patched, setPatched] = useState<Dataset | null>(null);
  useEffect(() => {
    setPatched(null);
  }, [data]);

  const dataset = patched ?? data;

  if (dataset === null) {
    return (
      <AppLayout title="List" actions={<BackToLists />}>
        <Card>
          {loadingFirst ? (
            <CardContent className="flex items-center gap-3 px-6 py-12 text-[0.92rem] text-muted">
              <Spinner size="md" label={null} />
              <span role="status">Loading this list…</span>
            </CardContent>
          ) : (
            <ErrorState error={error} title="Could not load this list" onRetry={reload} />
          )}
        </Card>
      </AppLayout>
    );
  }

  const status = DATASET_STATUS_DISPLAY[dataset.status];

  return (
    <AppLayout title={dataset.name} actions={<BackToLists />}>
      <div className="flex flex-col gap-6">
        {error !== null && (
          <FormAlert tone="error">
            {errorMessage(error)} What is shown below is the last version that loaded.
          </FormAlert>
        )}

        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-center gap-3">
              <DatasetStatusBadge status={dataset.status} />
              {dataset.sheetName !== null && (
                <Kicker as="span" className="flex items-center gap-1.5">
                  <FileSpreadsheet aria-hidden="true" className="size-3.5" />
                  sheet {dataset.sheetIndex + 1} · {dataset.sheetName}
                </Kicker>
              )}
            </div>

            <CardTitle>{dataset.name}</CardTitle>

            <CardDescription>
              From <span className="font-mono text-[0.82rem] text-text">
                {dataset.sourceFilename}
              </span>
              {dataset.sheetName !== null && (
                <>
                  , worksheet <span className="text-text">{dataset.sheetName}</span>
                </>
              )}{' '}
              · {formatFileSize(dataset.sourceBytes)} · uploaded {formatDate(dataset.createdAt)}
            </CardDescription>

            <CardDescription>{status.description}</CardDescription>
          </CardHeader>
        </Card>

        {dataset.status === 'pending' && (
          <Card>
            <CardHeader>
              <CardTitle>Map the columns</CardTitle>
              <CardDescription>
                Nothing from this file has been imported yet.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ColumnMappingForm
                key={dataset.id}
                dataset={dataset}
                onImportStarted={setPatched}
              />
            </CardContent>
          </Card>
        )}

        {dataset.status === 'processing' && (
          <Card>
            <CardHeader>
              <CardTitle>Importing</CardTitle>
            </CardHeader>
            <CardContent>
              {/* A settled import changes more than the counts — the mapping,
                  the error and the rejected-row report all become readable — so
                  the dataset is re-read rather than patched from the poll. */}
              <ImportProgress initial={dataset} onSettled={reload} />
            </CardContent>
          </Card>
        )}

        {dataset.status === 'failed' && <FailedImport dataset={dataset} onRetry={setPatched} />}

        {dataset.status === 'ready' && (
          <>
            <ImportSummary dataset={dataset} />
            <RejectedRows datasetId={dataset.id} />
            <ContactList datasetId={dataset.id} importedCount={dataset.importedCount} />
          </>
        )}
      </div>
    </AppLayout>
  );
}

function BackToLists() {
  return (
    <Link to="/audiences" className={buttonVariants({ variant: 'secondary', size: 'sm' })}>
      <ArrowLeft aria-hidden="true" className="size-4" />
      All lists
    </Link>
  );
}

/* -------------------------------------------------------------------------- */
/* Failed                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * A failed import is not a dead end: the API accepts a new mapping on a dataset
 * in `failed` precisely so a wrong choice can be corrected and run again. The
 * reason comes first, at full size, and the form underneath starts from the
 * mapping that failed rather than from nothing.
 */
function FailedImport({
  dataset,
  onRetry,
}: {
  dataset: Dataset;
  onRetry: (dataset: Dataset) => void;
}) {
  return (
    <Card className="border-bad/35">
      <CardHeader>
        <CardTitle>The import failed</CardTitle>
        <CardDescription>
          Nothing from this file was imported. Fix what the message points at, or change the
          mapping, and run it again.
        </CardDescription>
      </CardHeader>

      <CardContent className="flex flex-col gap-5">
        <FormAlert tone="error">
          {dataset.error ?? DATASET_STATUS_DISPLAY.failed.description}
        </FormAlert>

        <ColumnMappingForm
          key={dataset.id}
          dataset={dataset}
          onImportStarted={onRetry}
          submitLabel="Try the import again"
        />
      </CardContent>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* Ready — the summary                                                         */
/* -------------------------------------------------------------------------- */

function ImportSummary({ dataset }: { dataset: Dataset }) {
  const mapping = dataset.columnMapping ?? {};
  const byIndex = new Map(dataset.detectedColumns.map((column) => [column.index, column]));
  const extras = unmappedColumns(dataset.detectedColumns, mapping);
  const rejected = dataset.rejectedCount;

  return (
    <Card>
      <CardHeader>
        <CardTitle>What the import produced</CardTitle>
        {/* The description stated an invariant the three stats beneath it
            demonstrate arithmetically — rows in the sheet = imported + rejected
            — and which the very next card restates as its own title, "Rows that
            were not imported". Slice 29. */}
      </CardHeader>

      <CardContent className="flex flex-col gap-5">
        <MetricPanel
          kicker="Became contacts"
          figure={{ kind: 'measured', value: dataset.importedCount, unit: 'contacts' }}
          figureSize="lg"
          sub={
            <>
              from <span className="tabular-nums text-text">{formatCount(dataset.rowCount)}</span>{' '}
              rows in the sheet
            </>
          }
          {...(dataset.rowCount > 0
            ? { meter: { value: dataset.importedCount / dataset.rowCount } }
            : {})}
        >
          <MetricSection kicker="Every row accounted for">
            <dl className="flex flex-wrap gap-x-10 gap-y-4">
              <Stat label="Rows in the sheet" value={dataset.rowCount} />
              <Stat label="Imported" value={dataset.importedCount} />
              <Stat label="Rejected" value={rejected} />
            </dl>
          </MetricSection>
        </MetricPanel>

        {rejected > 0 && (
          <Callout
            tone="warn"
            density="compact"
            title={`${pluralise(rejected, 'row')} rejected`}
          >
            The report below gives the reason for each one.
          </Callout>
        )}

        <div className="flex flex-col gap-3">
          <Kicker as="h3" rule>
            How the columns were used
          </Kicker>

          <MappingList aria-label="How the columns of this sheet were used">
            {CONTACT_FIELD_VALUES.map((field) => {
              const index = mapping[field];
              const column = index === undefined ? undefined : byIndex.get(index);

              return (
                <MappingRow
                  key={field}
                  emphasis={column !== undefined}
                  arrowLabel={column === undefined ? 'for' : 'became'}
                  from={
                    column === undefined ? (
                      <span className="text-muted">No column</span>
                    ) : (
                      <MappingSource column={column.name} emphasis />
                    )
                  }
                  to={<FieldChip group="profile" label={CONTACT_FIELD_LABEL[field]} />}
                />
              );
            })}
            {extras.map((column) => (
              <MappingRow
                key={`extra-${String(column.index)}`}
                arrowLabel="was"
                from={<MappingSource column={column.name} />}
                to={<span className="text-muted">kept in extras, under its own name</span>}
              />
            ))}
          </MappingList>

          <p className="max-w-prose text-[0.86rem] leading-relaxed text-muted">
            {extras.length === 0 ? (
              <>Every column fed a contact field, so nothing was kept in extras.</>
            ) : (
              <>
                {pluralise(extras.length, 'unmapped column')}, preserved on every contact under its
                own name.
              </>
            )}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * One of the three row counts. Sans with tabular figures (slice 66): these are
 * counts a person adds up, and a dotted mono zero reads as an 8. None of the
 * three is coloured — the brand figure above already leads, the rejected
 * count's warning is the Callout's job in words, and a green "Imported" beside
 * an amber "Rejected" was a verdict painted on two numbers that are simply the
 * ledger.
 */
function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex flex-col gap-1">
      <Kicker as="dt">{label}</Kicker>
      <dd className="text-[1.25rem] font-semibold leading-none tracking-[-0.02em] tabular-nums text-text">
        {formatCount(value)}
      </dd>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Ready — the rejected-row report                                             */
/* -------------------------------------------------------------------------- */

/**
 * Why rows did not become contacts.
 *
 * Fetched even when `rejectedCount` is zero: "nothing was rejected" is a claim,
 * and it is worth making from the report rather than from a counter that could
 * disagree with it.
 */
function RejectedRows({ datasetId }: { datasetId: string }) {
  const { data: report, error, loadingFirst, reload } = useAsyncData(
    (signal) => fetchRejectedRows(datasetId, signal),
    [datasetId],
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Rows that were not imported</CardTitle>
        {/* The description enumerated, in prose, the column headers of the table
            immediately below it: Row / Reason / Column / Value / What happened.
            The table is self-describing, and this card IS the promise that
            nothing is discarded silently rather than a place to assert it.
            Slice 29. */}
      </CardHeader>

      <CardContent>
        {loadingFirst && (
          <div className="flex items-center gap-3 py-2 text-[0.92rem] text-muted">
            <Spinner size="md" label={null} />
            <span role="status">Loading the rejected-row report…</span>
          </div>
        )}

        {!loadingFirst && report === null && (
          <ErrorState
            error={error}
            title="Could not load the rejected-row report"
            onRetry={reload}
            className="flex flex-col items-start gap-3 py-2"
          />
        )}

        {report !== null && <RejectedRowReportView report={report} />}
      </CardContent>
    </Card>
  );
}

function RejectedRowReportView({ report }: { report: RejectedRowReport }) {
  const reasons = REJECTED_ROW_REASON_VALUES.map((reason) => ({
    reason,
    count: report.byReason[reason] ?? 0,
  })).filter((entry) => entry.count > 0);

  if (report.total === 0) {
    return (
      <p className="flex items-center gap-2 text-[0.92rem] text-muted">
        <Badge tone="good">None rejected</Badge>
        Every row in the sheet became a contact.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-3">
        <p className="text-[0.92rem] text-muted">
          {pluralise(report.total, 'row')} could not be imported.
        </p>

        <ul className="flex flex-col gap-2.5">
          {reasons.map(({ reason, count }) => {
            const display = REJECTED_ROW_REASON_DISPLAY[reason];
            return (
              <li key={reason} className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <Badge tone={display.tone}>{display.label}</Badge>
                <span className="text-[0.86rem] font-medium tabular-nums text-text">
                  {formatCount(count)}
                </span>
                <span className="text-[0.86rem] text-muted">{display.remedy}</span>
              </li>
            );
          })}
        </ul>
      </div>

      <TableContainer label="Rejected rows">
        <Table minWidth="46rem">
          <caption className="sr-only">
            Rejected rows, with the row number in the source file and the reason
          </caption>
          <TableHeader>
            <TableRow>
              <TableHeaderCell style={{ width: '6rem' }} align="right">
                Row
              </TableHeaderCell>
              <TableHeaderCell style={{ width: '11rem' }}>Reason</TableHeaderCell>
              <TableHeaderCell style={{ width: '12rem' }}>Column</TableHeaderCell>
              <TableHeaderCell>Value</TableHeaderCell>
              <TableHeaderCell>What happened</TableHeaderCell>
            </TableRow>
          </TableHeader>

          <TableBody>
            {report.rows.map((row) => {
              const display = REJECTED_ROW_REASON_DISPLAY[row.reason];
              return (
                <TableRow key={`${String(row.row)}-${row.reason}-${row.column ?? ''}`}>
                  <TableCell align="right" className="font-mono tabular-nums text-muted">
                    {row.row}
                  </TableCell>
                  <TableCell>
                    <Badge tone={display.tone}>{display.label}</Badge>
                  </TableCell>
                  <TableCell className="text-muted">
                    {row.column ?? <span className="text-muted">—</span>}
                  </TableCell>
                  <TableCell>
                    {row.value === undefined ? (
                      <span className="text-muted">—</span>
                    ) : (
                      <span className="font-mono text-[0.8rem] break-all text-text">
                        {row.value}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-muted">{row.message}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {report.truncated && (
        <p className="text-[0.86rem] leading-relaxed text-muted">
          Listing the first {formatCount(report.rows.length)} of {formatCount(report.total)} rejected
          rows. The counts above cover all of them.
        </p>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Ready — the contacts                                                        */
/* -------------------------------------------------------------------------- */

function ContactList({
  datasetId,
  importedCount,
}: {
  datasetId: string;
  importedCount: number;
}) {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<DataTableSort>({ key: 'createdAt', direction: 'desc' });

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [searchInput]);

  const sortField = contactSortSchema.catch('createdAt').parse(sort.key);

  const { data, error, refreshing, loadingFirst, reload } = useAsyncData(
    (signal) =>
      listContacts(
        {
          page,
          pageSize: DEFAULT_PAGE_SIZE,
          datasetId,
          sort: sortField,
          direction: sort.direction,
          ...(search === '' ? {} : { search }),
        },
        signal,
      ),
    [datasetId, page, search, sortField, sort.direction],
  );

  const columns = useMemo<Array<DataTableColumn<Contact>>>(
    () => [
      {
        key: 'name',
        header: 'Name',
        sortable: true,
        cell: (contact) =>
          contact.name === null ? (
            <span className="text-muted">No name in the file</span>
          ) : (
            <span className="text-text">{contact.name}</span>
          ),
      },
      {
        key: 'email',
        header: 'Email',
        sortable: true,
        cell: (contact) =>
          contact.email === null ? (
            <span className="text-muted">—</span>
          ) : (
            <span className="font-mono text-[0.8rem] break-all text-muted">{contact.email}</span>
          ),
      },
      {
        key: 'phone',
        header: 'Phone',
        width: '11rem',
        cell: (contact) =>
          contact.phoneE164 === null ? (
            <span className="text-muted">—</span>
          ) : (
            <span className="font-mono text-[0.8rem] whitespace-nowrap text-muted">
              {contact.phoneE164}
            </span>
          ),
      },
      {
        key: 'extras',
        header: 'Kept in extras',
        cell: (contact) => <Extras extras={contact.extras} />,
      },
      {
        key: 'createdAt',
        header: 'Added',
        sortable: true,
        width: '10rem',
        cell: (contact) => (
          <span className="whitespace-nowrap text-muted">{formatDateTime(contact.createdAt)}</span>
        ),
      },
    ],
    [],
  );

  const pagination = data?.pagination ?? null;
  const failedToLoad = error !== null && data === null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Contacts</CardTitle>
        <CardDescription>
          {pluralise(importedCount, 'contact')} came from this list. Unmapped columns were
          preserved, not dropped — they are under “kept in extras”.
        </CardDescription>
      </CardHeader>

      <CardContent className="flex flex-col gap-4">
        {failedToLoad ? (
          <ErrorState
            error={error}
            title="Could not load the contacts"
            onRetry={reload}
            className="flex flex-col items-start gap-3 py-2"
          />
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative min-w-0 flex-1 sm:max-w-xs">
                <Search
                  aria-hidden="true"
                  className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted"
                />
                <Input
                  type="search"
                  className="pl-9"
                  placeholder="Search name, email or phone"
                  aria-label="Search contacts in this list"
                  value={searchInput}
                  onChange={(event) => {
                    setSearchInput(event.target.value);
                  }}
                />
              </div>

              {refreshing && <Spinner size="sm" label="Updating the contacts" />}

              {pagination !== null && pagination.total > 0 && (
                <p className="ml-auto font-mono text-[0.7rem] uppercase tracking-[0.1em] text-muted">
                  {pluralise(pagination.total, 'contact')}
                </p>
              )}
            </div>

            {error !== null && (
              <FormAlert tone="error">
                {errorMessage(error)} The rows below are the last version that loaded.
              </FormAlert>
            )}

            <DataTable
              label="Contacts in this list"
              columns={columns}
              rows={data?.items ?? []}
              getRowKey={(contact) => contact.id}
              loading={loadingFirst}
              sort={sort}
              onSortChange={setSort}
              minWidth="52rem"
              empty={
                search === '' ? (
                  <p className="text-[0.9rem] text-muted">
                    This list produced no contacts. Every row is accounted for in the rejected-row
                    report above.
                  </p>
                ) : (
                  <p className="text-[0.9rem] text-muted">
                    No contact in this list matches <span className="text-text">“{search}”</span>.
                  </p>
                )
              }
              {...(pagination === null
                ? {}
                : {
                    pagination: {
                      page: pagination.page,
                      pageSize: pagination.pageSize,
                      total: pagination.total,
                      onPageChange: setPage,
                    },
                  })}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
}

/** How many unmapped columns this contact kept, and the first couple of them. */
function Extras({ extras }: { extras: ContactExtras }) {
  const entries = Object.entries(extras);

  if (entries.length === 0) {
    return <span className="text-muted">None</span>;
  }

  const shown = entries.slice(0, 2);
  const rest = entries.length - shown.length;
  const full = entries.map(([key, value]) => `${key}: ${extraText(value)}`).join('\n');

  return (
    <div className="flex min-w-0 flex-col gap-0.5" title={full}>
      {shown.map(([key, value]) => (
        <p key={key} className="truncate text-[0.8rem] text-muted">
          <span className="font-mono text-[0.72rem] uppercase tracking-[0.08em] text-muted">
            {key}
          </span>{' '}
          {extraText(value)}
        </p>
      ))}
      {rest > 0 && (
        <p className="font-mono text-[0.7rem] uppercase tracking-[0.1em] text-muted">
          +{formatCount(rest)} more
        </p>
      )}
    </div>
  );
}

/** A preserved cell as text. A blank stays visibly blank rather than reading `null`. */
function extraText(value: ExtraValue): string {
  if (value === null) return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return formatCount(value);
  return value;
}
